import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BookOpen,
  CircleUserRound,
  Compass,
  Info,
  Landmark,
  UserPlus,
  Volume2,
  Crown,
  Feather,
  ListChecks,
  Map,
  Newspaper,
  MousePointer2,
  ScrollText,
  Shield,
  Sparkles,
  Stamp,
  Trophy,
  Users,
} from "lucide-react";
import type { TutorialHomeView } from "./tutorial-session";

export interface WalkthroughPress {
  /** `data-tutorial` id of the real control the pointer presses. */
  target: string;
  /** Short invitation shown while waiting for the press. */
  prompt: string;
  /** Home panel the press opens (instead of the control's own handler). */
  view?: TutorialHomeView;
  /** The press moves on to the next step (which navigates). */
  next?: boolean;
  /** The press really clicks the control (in-page toggles, a choice). */
  opens?: boolean;
  /** `data-tutorial` id of what the press opens. It is folded away again
   *  when the step ends, so the next step starts from a clean page. */
  opened?: string;
  /** Explain first, then press (default: press first, then explain). */
  after?: boolean;
}

/** One stop on a scripted pointer path, played while a step is explained. */
export interface PointerStop {
  /** `data-tutorial` id the pointer travels to. */
  target: string;
  /** Where on the target it rests, as a fraction of its box (default centre). */
  fx?: number;
  fy?: number;
  /** How long it lingers there, in ms. */
  pause?: number;
  /** Really click it (select a card, pin a city). */
  click?: boolean;
  /** `data-tutorial` id of what the click opens; folded away when the step ends. */
  opened?: string;
  /** `data-tutorial` id of the control that folds `opened` away (a dialog's
   *  close button); by default the stop's own target is clicked again. */
  close?: string;
  /** Hover it as a mouse would (a region lights up), until the next stop. */
  hover?: boolean;
  /** Drag it sideways: scroll its content this many pixels to the right,
   *  over DRAG_MS. */
  drag?: number;
  /** Scroll it (or the panel it scrolls in) down this many pixels, slowly. */
  scroll?: number;
  /** How long that scroll takes, in ms (default 4000). */
  scrollMs?: number;
}

export const POINTER_MOVE_MS = 650;
/** A drag across the timeline: slow enough to follow, eased at both ends. */
export const DRAG_MS = 2600;
export const POINTER_PRESS_MS = 450;

export interface WalkthroughScene {
  /** Sets the card size and the minimum time on autoplay; the rest of the
   *  time comes from the length of `body` (see sceneDuration). */
  weight: "major" | "minor" | "glance";
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  Icon: LucideIcon;
  cue: "click" | "page" | "seal";
  route: string;
  /** Element(s) spotlighted once shown: "a+b" = union, "a,b" = first visible. */
  target: string;
  /** Replaces `target` below 640px wide, where some layouts stack. */
  mobileTarget?: string;
  homeView?: TutorialHomeView;
  press?: WalkthroughPress;
  /** News id the play page shows as a bulletin during this step. */
  newsSample?: string;
  /** A scripted pointer path instead of the idle reading drift. While it
   *  plays, the spotlight follows the pointer from stop to stop. */
  pointerTour?: PointerStop[];
  /** Spotlight once the pointer path is done (e.g. the card a click opened);
   *  otherwise the spotlight stays on the path's last stop. */
  focusAfterTour?: string;
  /** The spotlight stays on `target` while the pointer path plays, instead
   *  of following the pointer (hovering shields on the whole map). */
  tourKeepsSpot?: boolean;
  /** The pointer fades out once its path is done (nothing left to point at). */
  fadePointerAfterTour?: boolean;
  /** Autoplay reading time left after the pointer path, in ms (default:
   *  the rest of the step's reading time, at least 3 s). */
  afterTourMs?: number;
  /** Autoplay time for a step without a pointer path, instead of the
   *  reading-speed estimate (for steps that should stay brief). */
  durationMs?: number;
  /** While the pointer path plays, the card keeps clear of this (both house
   *  cards, say) even though the spotlight is on one part of it. */
  cardAround?: string;
}

const PLAY = "/chronicle/carolingian-empire/play?origin=frank&tutorial=1";

export const WALKTHROUGH_SCENES: WalkthroughScene[] = [
  {
    id: "atlas-tools",
    weight: "major",
    eyebrow: "Your atlas",
    title: "Everything begins on the map",
    body: "Every gold shield is a region of Europe, and each one holds several chronicles from its history. Point at a shield to see the lands it covers.",
    Icon: Compass, cue: "click", route: "/", target: "home-map", homeView: "atlas",
    tourKeepsSpot: true,
    fadePointerAfterTour: true,
    afterTourMs: 1500,
    // Low Countries, Central Europe, France: about 4.7 s, well inside the
    // step's own reading time, so the step is no longer than any other.
    pointerTour: [
      { target: "region-low-countries", fx: 0.68, fy: 0.74, pause: 900, hover: true },
      { target: "region-hre", fx: 0.68, fy: 0.74, pause: 900, hover: true },
      { target: "region-france", fx: 0.68, fy: 0.74, pause: 900, hover: true },
    ],
  },
  {
    id: "timeline",
    weight: "major",
    eyebrow: "Across the centuries",
    title: "Every chronicle on one timeline",
    body: "Each chronicle sits at its own years. Drag the timeline sideways to travel through the centuries, and use the region colours above it to pick out one part of Europe.",
    // The strip comes first: readiness is judged on it (the filter row is
    // drawn at 70% opacity and would never count as fully shown).
    Icon: ScrollText, cue: "page", route: "/", target: "timeline-scroller+timeline-filters", homeView: "atlas",
    press: { target: "home-timeline-btn", prompt: "Press Explore Timeline to open it.", view: "timeline" },
    // The spotlight holds still on the whole timeline while the pointer
    // drags it slowly, then rests on a region colour so the others dim.
    tourKeepsSpot: true,
    fadePointerAfterTour: true,
    afterTourMs: 2500,
    pointerTour: [
      { target: "timeline-scroller", fx: 0.78, fy: 0.55, pause: 900, drag: 420 },
      { target: "timeline-filter-central-europe", pause: 2000, hover: true },
    ],
  },
  {
    id: "achievements",
    weight: "minor",
    eyebrow: "Milestones",
    title: "Track your milestones",
    body: "Achievements follow your progress across regions, eras and the whole atlas, and the count at the top shows how far you have come. Some are only earned by those who go looking.",
    Icon: Trophy, cue: "click", route: "/", target: "achievements-panel", homeView: "atlas",
    press: { target: "home-achievements-btn", prompt: "Press Achievements to see the showcase.", view: "achievements" },
    tourKeepsSpot: true,
    fadePointerAfterTour: true,
    afterTourMs: 1200,
    pointerTour: [
      { target: "achievements-progress", fx: 1.05, fy: 1.1, pause: 1500 },
      { target: "achievements-list", fx: 0.8, fy: 0.55, pause: 300, scroll: 1400, scrollMs: 5500 },
    ],
  },
  {
    id: "settings-account",
    weight: "minor",
    eyebrow: "Settings",
    title: "Account and purchases",
    body: "A free account keeps your saves and purchases on every device. Three chronicles are free, and you can unlock the rest here.",
    Icon: UserPlus, cue: "click", route: "/", target: "settings-account+settings-purchases", homeView: "atlas", durationMs: 4500,
    press: { target: "home-settings-btn", prompt: "Press the gear to open settings.", view: "settings" },
  },
  {
    id: "settings-audio",
    weight: "glance",
    eyebrow: "Settings",
    title: "Sound and display",
    body: "Music volume, text size and full screen.",
    Icon: Volume2, cue: "page", route: "/", target: "settings-audio+settings-display", homeView: "settings",
  },
  {
    id: "settings-data",
    weight: "minor",
    eyebrow: "Settings",
    title: "Language and your data",
    body: "More languages are on the way. Anonymous play statistics help us improve the game, and you can switch them off here.",
    Icon: BarChart3, cue: "page", route: "/", target: "settings-language+settings-analytics", homeView: "settings", durationMs: 4500,
  },
  {
    id: "settings-help",
    weight: "minor",
    eyebrow: "Settings",
    title: "Help is always at hand",
    body: "Replay this tour, reread Good to know, or send us a message whenever you like. About tells you who makes the game.",
    Icon: Info, cue: "page", route: "/", target: "settings-about+settings-replay+settings-goodtoknow+settings-feedback", homeView: "settings", durationMs: 5000,
  },
  {
    id: "settings-reset",
    weight: "glance",
    eyebrow: "Settings",
    title: "Starting over",
    body: "Clear All Progress erases every save and record on this device. It asks before it does anything.",
    Icon: Info, cue: "page", route: "/", target: "settings-reset", homeView: "settings",
  },
  {
    id: "region",
    weight: "minor",
    eyebrow: "Choose a region",
    title: "Every shield holds several chronicles",
    body: "Each shield lists the chronicles of its region, with their years and your progress.",
    Icon: Shield, cue: "click", route: "/", target: "region-chronicles", homeView: "atlas",
    press: { target: "region-hre", prompt: "Press the Central Europe shield.", view: "region" },
  },
  {
    id: "chronicle-select",
    weight: "minor",
    eyebrow: "Choose a chronicle",
    title: "We will follow the Carolingian Empire",
    body: "Pick a chronicle to read its setting before you begin.",
    Icon: ListChecks, cue: "click", route: "/", target: "chronicle-carolingian-empire", homeView: "region",
    press: { target: "chronicle-carolingian-empire", prompt: "Press Rise of the Carolingian Empire.", next: true, after: true },
  },
  {
    id: "chronicle",
    weight: "minor",
    eyebrow: "Before the story",
    title: "Read the historical setting",
    body: "The Story sets the scene. Themes, and the endings and paths you have already found, are listed below it.",
    Icon: BookOpen, cue: "page", route: "/chronicle/carolingian-empire?tutorial=1", target: "chronicle-description",
  },
  {
    id: "protagonist",
    weight: "major",
    eyebrow: "Enter history",
    title: "Choose whose life you will live",
    body: "This chronicle follows two houses: a Frankish family rising with Charles Martel, and a free Saxon family standing in his way. For this tour we follow the Frankish house.",
    Icon: CircleUserRound, cue: "click", route: "/chronicle/carolingian-empire/protagonist?tutorial=1",
    target: "protagonist-card-frank", cardAround: "protagonist-card-frank+protagonist-card-saxon",
    pointerTour: [
      { target: "protagonist-card-frank", fx: 0.62, fy: 0.58, pause: 1200 },
      { target: "protagonist-card-saxon", fx: 0.62, fy: 0.58, pause: 1400 },
      { target: "protagonist-card-frank", fx: 0.5, fy: 0.45, pause: 700, click: true },
    ],
    press: { target: "protagonist-begin-btn", prompt: "Press Begin to enter the Frankish life.", next: true, after: true },
  },
  {
    id: "story",
    weight: "major",
    eyebrow: "Inside a chronicle",
    title: "Read the scene",
    body: "Each scene opens with the season, the year and the chapter, then tells you what this life is facing now.",
    Icon: Map, cue: "page", route: PLAY, target: "play-story",
  },
  {
    id: "character",
    weight: "major",
    eyebrow: "Who you are",
    title: "This is your character",
    body: "Who you are, where you come from and what your house wants. Lives end, and when one does, an heir can carry the story on.",
    Icon: Crown, cue: "page", route: PLAY, target: "play-character+play-character-profile", durationMs: 4000,
    press: { target: "play-character", prompt: "Press your character's name to see who they are.", opens: true, opened: "play-character-profile" },
  },
  {
    id: "terms",
    weight: "minor",
    eyebrow: "Look closer",
    title: "Names in colour hide more",
    body: "People, places and powers are coloured by the side they belong to. Tap an underlined name to learn who they were.",
    Icon: Feather, cue: "page", route: PLAY, target: "play-term+play-term-card",
    press: { target: "play-term", prompt: "Press the underlined name to see who they were.", opens: true, opened: "play-term-card" },
  },
  {
    id: "historian",
    weight: "minor",
    eyebrow: "Look closer",
    title: "Historian's notes",
    body: "Many scenes carry a note on what really happened, and on where the story takes liberties.",
    Icon: Landmark, cue: "page", route: PLAY, target: "play-historian",
    press: { target: "play-historian", prompt: "Press the Historian's Note to open it.", opens: true, opened: "play-historian-body" },
  },
  {
    id: "stats",
    weight: "major",
    eyebrow: "Your condition",
    title: "Five measures follow every decision",
    body: "Resources are your food, coin and supplies: run out and the life ends. Danger is how exposed you are, and at 100 it ends too. Community is who stands with you and Stability how settled you are. Influence is your standing: reach 100 and history may come to meet you. Tap the small i for a reminder.",
    Icon: BarChart3, cue: "page", route: PLAY, target: "play-status,play-measures",
  },
  {
    id: "choice",
    weight: "major",
    eyebrow: "Your decisions",
    title: "Choices shape a life, not history",
    body: "There is no score. Pick what this person would do and live with it. Some choices stay locked until your standing or your friendships are strong enough.",
    Icon: MousePointer2, cue: "click", route: PLAY, target: "play-choice-0+play-choice-1+play-choice-2+play-choice-3",
  },
  {
    id: "choice-made",
    weight: "major",
    eyebrow: "Your decisions",
    title: "Every choice has consequences",
    body: "The banner shows what your decision changed. Time moves on too: eleven years pass before the next scene.",
    Icon: Stamp, cue: "seal", route: PLAY, target: "play-outcome",
    press: { target: "play-choice-0", prompt: "Press the first choice to make it.", opens: true },
  },
  {
    id: "news",
    weight: "major",
    eyebrow: "The wider world",
    title: "News from beyond your road",
    body: "Bulletins arrive as the years pass: wars, deaths and treaties far from your own road. Some of them shift your measures.",
    Icon: Newspaper, cue: "page", route: PLAY, target: "play-news-frame", newsSample: "car_news_toulouse",
    press: { target: "news-continue", prompt: "Press Continue on the bulletin to return to your story.", opens: true, next: true, after: true },
  },
  {
    id: "news-archive",
    weight: "minor",
    eyebrow: "The wider world",
    title: "Every bulletin is kept",
    body: "News keeps every bulletin you have seen, so you can read it again at any time.",
    Icon: Newspaper, cue: "page", route: PLAY, target: "play-news-feed", durationMs: 4500,
    press: { target: "play-news-btn", prompt: "Press News to open every bulletin so far.", opens: true },
  },
  {
    id: "relationships",
    weight: "minor",
    eyebrow: "The side panel",
    title: "Relationships open doors",
    body: "Every group remembers how you treated it. Strong ties unlock choices others never see, and weak ones close them.",
    Icon: Users, cue: "page", route: PLAY, target: "play-relationships",
  },
  {
    id: "map",
    weight: "minor",
    eyebrow: "The side panel",
    title: "The map follows your road",
    body: "It marks where you are and every place this life has passed through. Tap a city to read what it was like at the time: who held it, and what it meant.",
    Icon: Map, cue: "page", route: PLAY, target: "play-map",
    pointerTour: [{ target: "play-map-current", pause: 500, click: true, opened: "play-map-city-card" }],
    focusAfterTour: "play-map-city-card",
  },
  {
    id: "journal",
    weight: "major",
    eyebrow: "Story so far",
    title: "Your life, written down",
    body: "Story keeps every scene you have lived and every choice you made in this life. It grows with each decision.",
    Icon: ScrollText, cue: "page", route: PLAY, target: "play-journal-panel", mobileTarget: "play-journal-entries,play-journal-panel",
    press: { target: "play-journal-btn", prompt: "Press Story to open the record of this life.", opens: true, opened: "play-journal-panel" },
  },
  {
    id: "legacy",
    weight: "major",
    eyebrow: "The Ledger",
    title: "Your journey is being recorded",
    body: "The Ledger keeps your choices, discoveries, encounters and endings across every chronicle. Take a moment to look around. This tour leaves your records untouched.",
    Icon: Sparkles, cue: "seal", route: "/", target: "ledger-panel", homeView: "atlas", durationMs: 6000,
    press: { target: "home-ledger-btn", prompt: "Press Ledger to open your records.", view: "ledger" },
  },
  {
    id: "ledger-continue",
    weight: "minor",
    eyebrow: "The Ledger",
    title: "Pick up where you left off",
    body: "Every chronicle has a card here. Once you have started one, its button reads Continue and takes you back to your last scene.",
    Icon: BookOpen, cue: "page", route: "/", target: "ledger-card-play", homeView: "ledger", durationMs: 5500,
  },
  {
    id: "ledger-progress",
    weight: "minor",
    eyebrow: "The Ledger",
    title: "Every chronicle keeps its own record",
    body: "Progress opens the endings, paths and discoveries you have found in that chronicle, and the figures you have met.",
    Icon: Trophy, cue: "page", route: "/", target: "ledger-card-progress", homeView: "ledger",
    pointerTour: [{ target: "ledger-card-progress", pause: 700, click: true, opened: "progress-dialog", close: "progress-close" }],
    focusAfterTour: "progress-dialog",
    afterTourMs: 4000,
  },
  {
    id: "all-set",
    weight: "minor",
    eyebrow: "All set",
    title: "Your atlas awaits",
    body: "You know the way around now. Pick a shield, choose a life, and see how you weather the centuries. You can replay this tour from Settings.",
    Icon: Compass, cue: "seal", route: "/", target: "", homeView: "atlas",
  },
];

const MIN_DURATION_MS = { major: 5000, minor: 3500, glance: 2500 } as const;

/** How long a step's scripted pointer path takes to play. */
export function pointerTourMs(scene: WalkthroughScene): number {
  return (scene.pointerTour ?? []).reduce((ms, stop) => ms + POINTER_MOVE_MS + (stop.pause ?? 500) + (stop.click ? POINTER_PRESS_MS : 0) + (stop.drag ? DRAG_MS : 0) + (stop.scroll ? stop.scrollMs ?? 4000 : 0), 0);
}

/** Autoplay reading time: 1.5 s to find the spotlight, then ~220 words a minute. */
export function sceneDuration(scene: WalkthroughScene): number {
  const words = scene.body.trim().split(/\s+/).length;
  return Math.max(MIN_DURATION_MS[scene.weight], 1500 + words * 270);
}
